// @ts-check

import { format, parse, toDate } from 'date-fns'

// Value format
export const FORMAT_VALUE_DATE_TIME = 'yyyy-MM-dd HH:mm'
export const FORMAT_VALUE_DATE_TIME_SECONDS = `${FORMAT_VALUE_DATE_TIME}:ss`
export const FORMAT_VALUE_DATE = 'yyyy-MM-dd'
export const FORMAT_VALUE_TIME = 'HH:mm'

// Display format
export const FORMAT_DISPLAY_DATE_TIME = 'dd.MM.yyyy HH:mm'
export const FORMAT_DISPLAY_DATE_TIME_SECONDS = `${FORMAT_DISPLAY_DATE_TIME}:ss`
export const FORMAT_DISPLAY_DATE = 'dd.MM.yyyy'
export const FORMAT_DISPLAY_TIME = 'HH:mm'

/**
 * Returns given date string as date, uses given format for parsing
 *
 * @param {string} dateString
 * @param {string} formatString
 * @returns {Date}
 */
export function stringToDate(dateString, formatString = FORMAT_VALUE_DATE) {
  return parse(dateString, formatString, new Date())
}

/**
 * Returns given date as a string in given format
 * @deprecated use formatDate or normalizeDate instead
 *
 * @param {import('date-fns').DateArg<Date>} date
 * @param {string} formatString
 * @returns {string}
 */
export function dateToString(date, formatString = FORMAT_DISPLAY_DATE) {
  return format(date, formatString)
}

/**
 * Returns given datetime as a string
 * @deprecated use formatDatetime or normalizeDatetime instead
 *
 * @param {import('date-fns').DateArg<Date>} datetime
 * @returns {string}
 */
export function dateTimeToString(
  datetime,
  formatString = FORMAT_DISPLAY_DATE_TIME_SECONDS
) {
  return format(datetime, formatString)
}

/**
 * Reformats given date string from one format to another
 *
 * @param {string} dateString
 * @param {string} fromFormatString
 * @param {string} toFormatString
 * @returns {string}
 */
export function reformatDateString(
  dateString,
  fromFormatString = FORMAT_VALUE_DATE,
  toFormatString = FORMAT_DISPLAY_DATE
) {
  const date = stringToDate(dateString, fromFormatString)

  return dateToString(date, toFormatString)
}

/**
 * Returns a function that reformats a date string to the specified format.
 * @param {Parameters<typeof format>[1]} toFormat - The desired date format (e.g., 'dd.MM.yyyy').
 * @returns {(date: Parameters<typeof toDate>[0], fallback?: string) => string} - A function that takes a date string and returns it in the specified format.
 */
const dateFormatter = (toFormat) => (date, fallback) => {
  if (!date) {
    if (fallback !== undefined) {
      return fallback
    }
    throw new Error('No date provided')
  }

  const asDate = toDate(date)

  if (asDate.toString() === 'Invalid Date') {
    if (fallback !== undefined) {
      return fallback
    }
    throw new Error(`Invalid date: ${date}`)
  }

  const formattedDate = format(asDate, toFormat)

  return formattedDate
}

export const formatDate = dateFormatter('dd.MM.yyyy')

export const normalizeDate = dateFormatter('yyyy-MM-dd')

export const formatDatetime = dateFormatter('dd.MM.yyyy HH:mm:ss')

export const normalizeDatetime = dateFormatter('yyyy-MM-dd HH:mm:ss')
